// src/components/blog/BlogPostDetail.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { marked } from 'marked';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

// Import DOMPurify for HTML sanitization
import DOMPurify from 'dompurify';
import { BlogContext } from '../../contexts/BlogContext';
import { ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import './BlogPost.css'; // Import the CSS for blog post content

function BlogPostDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getPostById } = useContext(BlogContext);
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function loadPost() {
      try {
        setLoading(true);
        if (!slug) {
          setError('Post not found');
          return;
        }
        
        const postData = await getPostById(slug);
        
        if (!postData) {
          setError('Post not found');
          return;
        }
        
        setPost(postData);
      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load the blog post');
      } finally {
        setLoading(false);
      }
    }
    
    loadPost();
  }, [slug, getPostById]);
  
  // Parse markdown content
  const renderContent = (content) => {
    if (!content) return '';
    
    // Configure marked with options
    marked.setOptions({
      breaks: true,           // Add line breaks on \n
      gfm: true,              // GitHub Flavored Markdown
      headerIds: true,        // Include IDs in headers
      headerPrefix: 'blog-',  // Prefix for header IDs
      mangle: true,           // Escape HTML to prevent XSS
      smartLists: true,       // Use smarter list behavior
      smartypants: true,      // Use "smart" typographic punctuation
      xhtml: false            // Don't use XHTML closing tags
    });
    
      // First parse the markdown to HTML with marked, then sanitize with DOMPurify
    return { __html: DOMPurify.sanitize(marked(content)) };
  };
  
  // Share functionality
  const shareUrl = `${window.location.origin}/blog/${slug}`;
  
  const shareOnFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };
  
  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`,
      '_blank'
    );
  };
  
  const shareOnLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      '_blank'
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
        <p className="text-gray-700 mb-6">{error || 'Post not found'}</p>
        <Link
          to="/blog"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* SEO Metadata */}
      <Helmet>
        <title>{post.title} | ReelIntro Blog</title>
        <meta name="description" content={post.excerpt || post.content?.substring(0, 160)} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || post.content?.substring(0, 160)} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        {post.imageUrl && <meta property="og:image" content={post.imageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": "${post.title}",
            "description": "${post.excerpt || post.content?.substring(0, 160)}",
            "datePublished": "${post.publishDate?.toISOString() || new Date().toISOString()}",
            ${post.imageUrl ? `"image": "${post.imageUrl}",` : ''}
            "author": {
              "@type": "Organization",
              "name": "ReelIntro"
            },
            "publisher": {
              "@type": "Organization",
              "name": "ReelIntro",
              "logo": {
                "@type": "ImageObject",
                "url": "https://reelintro.com/logo512.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "${shareUrl}"
            }
          }
        `}</script>
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        {/* Back Link */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
          </button>
        </div>
        
        <article className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto">
          {/* Article Header */}
          <header className="p-8 border-b border-gray-200">
            {post.category && (
              <div className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2">
                {post.category}
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {post.title}
            </h1>
            <div className="flex items-center justify-between text-gray-600">
              <div>
                {post.publishDate && (
                  <time dateTime={post.publishDate.toISOString()} className="text-sm">
                    Published: {format(post.publishDate, 'MMMM d, yyyy')}
                  </time>
                )}
              </div>
              
              {/* Share Buttons */}
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium hidden md:inline-block">Share:</span>
                <button 
                  onClick={shareOnFacebook}
                  className="text-gray-500 hover:text-blue-800" 
                  aria-label="Share on Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </button>
                <button 
                  onClick={shareOnTwitter}
                  className="text-gray-500 hover:text-blue-400" 
                  aria-label="Share on Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </button>
                <button 
                  onClick={shareOnLinkedIn}
                  className="text-gray-500 hover:text-blue-600" 
                  aria-label="Share on LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </button>
              </div>
            </div>
          </header>
          
          {/* Featured Image */}
          {post.imageUrl && (
            <div className="w-full">
              <img 
                src={post.imageUrl} 
                alt={post.title} 
                className="w-full h-auto max-h-96 object-cover"
                loading="lazy"
              />
            </div>
          )}
          
          {/* Article Content */}
          <div className="p-8">
            <div 
              className="blog-content prose prose-lg prose-headings:text-gray-800 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-a:text-blue-600 hover:prose-a:text-blue-800 prose-a:transition-colors prose-a:underline prose-img:rounded-lg prose-img:my-8 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-700 prose-strong:font-bold prose-code:bg-gray-100 prose-code:p-1 prose-code:rounded prose-code:text-sm prose-pre:bg-gray-800 prose-pre:text-white prose-pre:p-4 prose-pre:rounded-lg max-w-none mx-auto"
              dangerouslySetInnerHTML={renderContent(post.content)}
            ></div>
          </div>
          
          {/* Article Footer */}
          <footer className="p-8 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="mb-4 md:mb-0">
                <Link to="/blog" className="text-blue-600 hover:text-blue-800 font-medium">
                  ← Back to all articles
                </Link>
              </div>
              
              {/* Mobile Share */}
              <div className="md:hidden">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Share this article:</h3>
                <div className="flex space-x-6">
                  <button 
                    onClick={shareOnFacebook}
                    className="text-gray-500 hover:text-blue-800" 
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={shareOnTwitter}
                    className="text-gray-500 hover:text-blue-400" 
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={shareOnLinkedIn}
                    className="text-gray-500 hover:text-blue-600" 
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* Desktop Share Button */}
              <div className="hidden md:block">
                <button
                  onClick={async () => {
                    try {
                      // Check if clipboard permissions are granted or prompt for them
                      if (navigator.clipboard) {
                        await navigator.clipboard.writeText(shareUrl);
                        toast ? toast.success('Link copied to clipboard!') : alert('Link copied to clipboard!');
                      } else {
                        // Fallback for browsers that don't support Clipboard API
                        const textArea = document.createElement('textarea');
                        textArea.value = shareUrl;
                        document.body.appendChild(textArea);
                        textArea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textArea);
                        toast ? toast.success('Link copied to clipboard!') : alert('Link copied to clipboard!');
                      }
                    } catch (err) {
                      console.error('Failed to copy: ', err);
                      toast ? toast.error('Failed to copy link') : alert('Failed to copy link. Please try manually selecting and copying the URL.');
                    }
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Share2 className="mr-2 h-4 w-4" /> Share Article
                </button>
              </div>
            </div>
          </footer>
        </article>
        
        {/* CTA Card */}
        <div className="max-w-4xl mx-auto mt-12 bg-blue-700 text-white rounded-lg shadow-lg p-8">
          <div className="md:flex items-center justify-between">
            <div className="mb-6 md:mb-0 md:w-2/3">
              <h2 className="text-2xl font-bold mb-2">Ready to create your video introduction?</h2>
              <p className="text-blue-100">
                Make a lasting impression with a professional video intro for your next job application.
              </p>
            </div>
            <div>
              <Link
                to="/signup"
                className="inline-block bg-white text-blue-700 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                Record Your Video Cover Letter Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlogPostDetail;